// Fee mode — the one switch between fee-routed and direct payments.
// 'fee'    : in-app EVM payments route through the ChainSaleFee contract where deployed (sender surcharge
//            + recipient deduction, split on-chain). Tokens = approve + forwardToken (two wallet prompts).
// 'direct' : payer → merchant, exact amount, no fee or surcharge. Tokens = one plain transfer.
// Build-time only (VITE_PAYMENT_FEE_MODE) — deliberately not a runtime toggle a customer could flip.
// Payment-QR (wallet-scanned) payments are always direct; Solana/TON/Tron have no fee path yet (task 151).
// Everything fee-specific hangs off this one switch, so 'direct' turns it all off without removing it:
//   - EVM senders route through ChainSaleFee (resolveFeeRoute → sendEvmTransaction / approveEvmFeeSpend)
//   - pay step: fee notice + split, two-tap Allow → Pay for tokens, revoke.cash help (planFeeNotice → null)
//   - direct-wallet payment QR (the WalletConnect / wallet-adapter fallback): always offered when fees are
//     off; on EVM with fees on it waits for the merchant's unlock link (isDirectQrAllowed)
//   - Send page $0.50 minimum (fee economics only)

import type { NetworkId } from '../types/network.ts';
import { getFeeContractAddress } from './feeCalc.ts';

export type FeeMode = 'fee' | 'direct';

// Fail-safe: only an explicit 'direct' turns fees off — unset or mistyped values keep fees on
export const parseFeeMode = (raw: string | undefined): FeeMode =>
  raw?.trim().toLowerCase() === 'direct' ? 'direct' : 'fee';

export const FEE_MODE: FeeMode = parseFeeMode(import.meta.env.VITE_PAYMENT_FEE_MODE as string | undefined);

export const feesEnabled = (mode: FeeMode = FEE_MODE): boolean => mode === 'fee';

// Fee contract to route through, or null for a direct payment (fees off, or no contract on this network)
export const resolveFeeRoute = (networkId: NetworkId, mode: FeeMode = FEE_MODE): string | null =>
  feesEnabled(mode) ? getFeeContractAddress(networkId) : null;
